import { supabase } from '../lib/supabase';

export class ClientService {
  static async getAllClients() {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select(`
          id,
          name,
          client_status,
          services_provided,
          lead_source,
          address,
          client_of,
          brand,
          trimble_formats,
          reason,
          company_phone,
          yearly_cost,
          billing_status,
          platform,
          pricing_status,
          terms,
          acquisition_source,
          dealer_name,
          contractor_name,
          three_d_utilities,
          level_of_detail,
          needs_handicap_ramps,
          platform_other,
          w9_coi_email,
          clients_invoiced_date,
          first_model_date,
          first_takeoff_date,
          state
        `)
        .order('name');

      if (error) {
        throw new Error(`Failed to fetch clients: ${error.message}`);
      }

      // Get contact count for each client
      const clientsWithCounts = await Promise.all(
        data.map(async (client) => {
          const { count: contactCount } = await supabase
            .from('contacts')
            .select('*', { count: 'exact', head: true })
            .eq('client_id', client.id);

          return {
            id: client.id,
            name: client.name,
            clientStatus: client.client_status || 'Active Client',
            servicesProvided: client.services_provided || [],
            leadSource: client.lead_source || '',
            address: client.address || '',
            clientOf: client.client_of || '',
            brand: client.brand || [],
            trimbleFormats: client.trimble_formats || [],
            reason: client.reason || '',
            contactCount: contactCount || 0,
            companyPhone: client.company_phone || '',
            yearlyCost: client.yearly_cost || null,
            billingStatus: client.billing_status || '',
            platform: client.platform || '',
            pricingStatus: client.pricing_status || '',
            terms: client.terms || null,
            acquisitionSource: client.acquisition_source || '',
            dealerName: client.dealer_name || '',
            contractorName: client.contractor_name || '',
            threeDUtilities: client.three_d_utilities || '',
            levelOfDetail: client.level_of_detail || '',
            needsHandicapRamps: client.needs_handicap_ramps || false,
            platformOther: client.platform_other || '',
            w9CoiEmail: client.w9_coi_email || '',
            clientsInvoicedDate: client.clients_invoiced_date || null,
            firstModelDate: client.first_model_date || null,
            firstTakeoffDate: client.first_takeoff_date || null,
            state: client.state || ''
          };
        })
      );

      return clientsWithCounts;
    } catch (error) {
      console.error('Error fetching clients:', error);
      throw error;
    }
  }

  static async createClient(clientData) {
    try {
      const insertData = {
        name: clientData.name,
        client_status: clientData.clientStatus || 'Active Client',
        services_provided: clientData.servicesProvided || null,
        lead_source: clientData.leadSource || null,
        address: clientData.address || null,
        client_of: clientData.clientOf || null
      };

      const { data, error } = await supabase
        .from('clients')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to create client: ${error.message}`);
      }

      return {
        id: data.id,
        name: data.name,
        clientStatus: data.client_status || 'Active Client',
        servicesProvided: data.services_provided || [],
        leadSource: data.lead_source || '',
        address: data.address || '',
        clientOf: data.client_of || '',
        brand: data.brand || [],
        trimbleFormats: data.trimble_formats || [],
        reason: data.reason || '',
        contactCount: 0
      };
    } catch (error) {
      console.error('Error creating client:', error);
      throw error;
    }
  }

  static async updateClient(clientId, updates) {
    try {
      const updateData = {};

      // Map frontend field names to database column names
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.clientStatus !== undefined) updateData.client_status = updates.clientStatus;
      if (updates.servicesProvided !== undefined) updateData.services_provided = updates.servicesProvided;
      if (updates.leadSource !== undefined) updateData.lead_source = updates.leadSource;
      if (updates.address !== undefined) updateData.address = updates.address;
      if (updates.clientOf !== undefined) updateData.client_of = updates.clientOf;
      if (updates.brand !== undefined) updateData.brand = updates.brand;
      if (updates.trimbleFormats !== undefined) updateData.trimble_formats = updates.trimbleFormats;
      if (updates.state !== undefined) updateData.state = updates.state;
      if (updates.companyPhone !== undefined) updateData.company_phone = updates.companyPhone;
      if (updates.reason !== undefined) updateData.reason = updates.reason;
      if (updates.levelOfDetail !== undefined) updateData.level_of_detail = updates.levelOfDetail || null;
      if (updates.needsHandicapRamps !== undefined) updateData.needs_handicap_ramps = updates.needsHandicapRamps;

      const { data, error } = await supabase
        .from('clients')
        .update(updateData)
        .eq('id', clientId)
        .select()
        .single();
      if (error) {
        throw new Error(`Failed to update client: ${error.message}`);
      }

      // Get updated contact count
      const { count: contactCount } = await supabase
        .from('contacts')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', clientId);

      return {
        id: data.id,
        name: data.name,
        clientStatus: data.client_status || 'Active Client',
        servicesProvided: data.services_provided || [],
        leadSource: data.lead_source || '',
        address: data.address || '',
        clientOf: data.client_of || '',
        brand: data.brand || [],
        trimbleFormats: data.trimble_formats || [],
        reason: data.reason || '',
        state: data.state || '',
        companyPhone: data.company_phone || '',
        levelOfDetail: data.level_of_detail || '',
        needsHandicapRamps: data.needs_handicap_ramps || false,
        contactCount: contactCount || 0
      };
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  }

  static async deleteClient(clientId) {
    try {
      // Check if client has any contacts
      const { count: contactCount } = await supabase
        .from('contacts')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', clientId);

      if (contactCount > 0) {
        throw new Error(
          `Cannot delete client: ${contactCount} contacts are linked to this client`
        );
      }

      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId);

      if (error) {
        throw new Error(`Failed to delete client: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error('Error deleting client:', error);
      throw error;
    }
  }

  static async getClientById(clientId) {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .single();

      if (error) {
        throw new Error(`Failed to fetch client: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error fetching client:', error);
      throw error;
    }
  }

  // Get clients with financial analytics and project data
  static async getClientsWithAnalytics() {
    try {
      const { data, error } = await supabase.rpc('get_clients_with_analytics');

      if (error) {
        console.error('RPC error, falling back to manual query:', error);
        // Fallback to manual aggregation if RPC function doesn't exist
        return await this.getClientsWithAnalyticsManual();
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching clients with analytics:', error);
      throw error;
    }
  }

  // Manual fallback for getClientsWithAnalytics if RPC function doesn't exist
  static async getClientsWithAnalyticsManual() {
    try {
      // Get all clients first
      const { data: clients, error: clientsError } = await supabase
        .from('clients')
        .select('*')
        .order('name');

      if (clientsError) {
        throw new Error(`Failed to fetch clients: ${clientsError.message}`);
      }

      // Get all projects for aggregation
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('client_id, initial_acceptance, model_price, completed_date');

      if (projectsError) {
        throw new Error(`Failed to fetch projects: ${projectsError.message}`);
      }

      const currentYear = new Date().getFullYear();
      const lastYear = currentYear - 1;

      // Aggregate data manually
      const clientsWithAnalytics = clients.map((client) => {
        const clientProjects = projects.filter((p) => p.client_id === client.id);

        const projectCount = clientProjects.length;
        const lastJobDate = clientProjects
          .filter((p) => p.initial_acceptance)
          .sort((a, b) => new Date(b.initial_acceptance) - new Date(a.initial_acceptance))[0]?.initial_acceptance || null;

        const daysSinceLastJob = lastJobDate
          ? Math.floor((new Date() - new Date(lastJobDate)) / (1000 * 60 * 60 * 24))
          : null;

        const totalThisYear = clientProjects
          .filter((p) => p.initial_acceptance && new Date(p.initial_acceptance).getFullYear() === currentYear)
          .reduce((sum, p) => sum + (parseFloat(p.model_price) || 0), 0);

        const totalLastYear = clientProjects
          .filter((p) => p.initial_acceptance && new Date(p.initial_acceptance).getFullYear() === lastYear)
          .reduce((sum, p) => sum + (parseFloat(p.model_price) || 0), 0);

        const totalAllTime = clientProjects
          .reduce((sum, p) => sum + (parseFloat(p.model_price) || 0), 0);

        return {
          ...client,
          project_count: projectCount,
          last_job_date: lastJobDate,
          days_since_last_job: daysSinceLastJob,
          total_this_year: totalThisYear,
          total_last_year: totalLastYear,
          total_all_time: totalAllTime
        };
      });

      return clientsWithAnalytics;
    } catch (error) {
      console.error('Error in manual analytics aggregation:', error);
      throw error;
    }
  }

  // Configuration options for dropdowns and multi-selects
  static getFieldOptions() {
    return {
      clientStatus: ['Active Client', 'Part Time Client', 'Dropped', 'Lost Client', 'Name Change'],
      servicesProvided: [
        '3D Models',
        'Takeoffs'
      ],
      brand: [
        'Trimble',
        'Topcon',
        'Leica',
        'Takeoffs',
        'Unknown'
      ],
      trimbleFormats: [
        'dxf/ttm',
        'svd/svl',
        'VCL'
      ],
      reason: [
        'Lost Contact',
        'New Management',
        'Bad Fit',
        'Hired in House',
        'Local Model Builder',
        'Pricing',
        'Out of Business',
        'Lack of Payment',
        'Other'
      ],
      leadSource: [
        'Client Referral',
        'Email Text Marketing',
        'Instagram',
        'James River',
        'Linkedin',
        'Previous Contact',
        'Search Engine',
        'SITECH Chesapeake',
        'SITECH Horizon',
        'SITECH Midway',
        'SITECH North Central Florida',
        'SITECH Northeast',
        'SITECH Pacific',
        'SITECH Precision',
        'Southeastern Equipment',
        'TikTok',
        'YouTube',
        'Name Change'
      ],
      levelOfDetail: [
        'Level 1 - Machine Grading',
        'Level 2 - Balanced',
        'Level 3 - Survey Grade'
      ]
    };
  }
}